/// <amd-module name="Search/Shared/Base/LocalSettingsService" />
import { Services, VssService, IVssService, IVssPageContext } from "VSS/Platform/Context";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { format } from "VSS/Core/Util/String";
import { ITfsPageData, getTFSPageData } from "Tfs/Platform/Page";
import { IPageData, IVssPageService } from "VSS/Platform/Page";


export interface ISearchLocalSettingsService extends IVssService {
    getValue<T>(key: string, defaultValue: T, scope?: Scope): T;

    setValue(key: string, value: any, scope?: Scope): void;

    clear(key: string, scope: Scope): void;
}

export enum Scope {
    Collection = 1,

    Project = 2
}

class SearchLocalSettingsService extends VssService implements ISearchLocalSettingsService {
    public getValue<T>(key: string, defaultValue: T, scope?: Scope): T {
        let value: T = defaultValue;

        if (window.localStorage) {
            const keyValue = window.localStorage.getItem(this.getScopedKey(key, scope));

            try {
                if (keyValue) {
                    value = (JSON.parse(keyValue)).value as T;
                }
            } catch (error) {
                // No-op
            }
        }

        return value;
    }

    public setValue(key: string, value: any, scope?: Scope): void {
        if (window.localStorage) {
            try {
                window.localStorage.setItem(this.getScopedKey(key, scope), JSON.stringify({ value }));
            } catch (error) {
                // No-op
            }
        }
    }

    public clear(key: string, scope?: Scope): void {
        if (window.localStorage) {
            window.localStorage.removeItem(this.getScopedKey(key, scope));
        }
    }

    private getScopedKey = (key: string, scope?: Scope): string => {
        let scopeString: string | undefined;

        if (scope) {
            if (Scope.Collection === scope) {
                scopeString = `collection/${this.getCollectionName()}`;
            } else {
                scopeString = `project/${this.getProjectName()}`;
            }
        }


        return scopeString ? format("{0}/{1}", scopeString, key) : key;
    }

    private getProjectName(): string | undefined {
        const pageData: ITfsPageData | undefined = getTFSPageData(this.pageContext);
        return pageData && pageData.project && pageData.project.name;
    }

    private getCollectionName(): string | undefined {
        const pageData: IPageData | undefined = this.pageContext.getService<IVssPageService>("IVssPageService").getData();
        return pageData && pageData.hostName;
    }
}

/**
 * Method to get settings value for given key using ISettingsService
 */
export function getSetting<T>(pageContext: IVssPageContext, key: string, defaultValue: T): T {
    const settingsService = pageContext.getService<ISettingsService>("ISettingsService");
    const value = settingsService.getEntry<T>(key, SettingsUserScope.Host);
    if (value == null) {
        return defaultValue;
    }
    return value;
}

Services.add("ISearchLocalSettingsService", { serviceFactory: SearchLocalSettingsService });